import { type CTWRequestContext, type CTWState, useCTW } from '@ctw/shared/context/ctw-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { usePatientContext, usePatientQuery } from '@ctw/shared/context/patient-provider';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { getApiServiceBaseUrl } from '@ctw/shared/utils/environments';
import { QUERY_KEY_AI_CHART_REVIEW_SUMMARY } from '@ctw/shared/utils/query-keys';
import { retryWithExponentialBackoff } from '@ctw/shared/utils/request';
import { useState } from 'react';
import type { ChartReviewResponse } from './types';
import { translateUnixBreaksToHtml } from './utils';

const MAX_RETRIES = 5;
const BASE_DELAY = 2000;

// get a summary via the new API, does not use the just-in-time API
const getSummaryViaNewApi = async (
  ctwFetch: CTWState['ctwFetch'],
  requestContext: CTWRequestContext,
  upid: string,
  summaryType: string,
): Promise<ChartReviewResponse> => {
  const endpoint = `${getApiServiceBaseUrl({
    service: 'api.ai',
    env: requestContext.env,
  })}/patients/${upid}/summary/${summaryType}`;
  const resp = await ctwFetch<ChartReviewResponse>(endpoint, {
    headers: {
      Authorization: `Bearer ${requestContext.authToken}`,
      'Content-Type': 'application/json',
      'Zus-Account': requestContext.builderId,
    },
    method: 'GET',
  });
  resp.data.hpi = translateUnixBreaksToHtml(resp.data.hpi);
  return resp.data;
};

const justInTimeSummary = async (
  ctwFetch: CTWState['ctwFetch'],
  requestContext: CTWRequestContext,
  upid: string,
  summaryType: string,
): Promise<ChartReviewResponse> => {
  const endpoint = `${getApiServiceBaseUrl({ env: requestContext.env, service: 'api.ai' })}/patients/${upid}/summaries/${summaryType}`;

  const resp = await ctwFetch<ChartReviewResponse>(endpoint, {
    headers: {
      Authorization: `Bearer ${requestContext.authToken}`,
      'Content-Type': 'application/json',
      'Zus-Account': requestContext.builderId,
    },
    method: 'GET',
  });

  resp.data.hpi = translateUnixBreaksToHtml(resp.data.hpi);
  return resp.data;
};

const triggerSummaryGeneration = async (
  ctwFetch: CTWState['ctwFetch'],
  requestContext: CTWRequestContext,
  upid: string,
  summaryType: string,
  useNewAIApi: boolean,
): Promise<ChartReviewResponse> => {
  const endpoint = `${getApiServiceBaseUrl({ service: 'api.ai', env: requestContext.env })}/patients/${upid}/${useNewAIApi ? 'summary' : 'summaries'}/${summaryType}`;

  const resp = await ctwFetch<ChartReviewResponse>(endpoint, {
    headers: {
      Authorization: `Bearer ${requestContext.authToken}`,
      'Content-Type': 'application/json',
      'Zus-Account': requestContext.builderId,
    },
    method: 'POST',
  });

  resp.data.hpi = translateUnixBreaksToHtml(resp.data.hpi);
  return resp.data;
};

export const ErrorSummary: ChartReviewResponse = {
  hpi: 'An error occurred while fetching the summary',
  citations: [],
};

export const NotFoundSummaryHPI = '<br>Patient is not indexed. Please contact support';

export const useSummaryForPatient = (summaryType: string | null) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const telemetry = useTelemetry();

  const { requestContext, ctwFetch } = useCTW();
  const { patient } = usePatientContext();
  const useNewAIApi = useFeatureFlag('ctw-focused-summary');

  const {
    data: document,
    isLoading,
    error,
    refetch,
  } = usePatientQuery({
    queryId: QUERY_KEY_AI_CHART_REVIEW_SUMMARY,
    queryKey: [summaryType],
    queryFn: async ({ requestContext, patient }) => {
      if (!summaryType) {
        return ErrorSummary;
      }
      try {
        const upid = patient.UPID ?? '';

        const resp = useNewAIApi
          ? await getSummaryViaNewApi(ctwFetch, requestContext, upid, summaryType)
          : await justInTimeSummary(ctwFetch, requestContext, upid, summaryType);

        resp.upid = patient.UPID;
        return resp;
      } catch {
        return ErrorSummary;
      }
    },
  });

  const getSummary = async (summaryType: string | null) => {
    if (!summaryType) {
      return;
    }
    setIsGenerating(true);
    try {
      const upid = patient.UPID || 'unknown-upid';

      if (useNewAIApi) {
        await getSummaryViaNewApi(ctwFetch, requestContext, upid, summaryType);
      } else {
        await justInTimeSummary(ctwFetch, requestContext, upid, summaryType);
      }

      // Wait for the summary to be generated
      return retryWithExponentialBackoff(
        async () => {
          const resp = await refetch();
          if (resp.data?.hpi === NotFoundSummaryHPI) {
            throw new Error('Summary not found');
          }
          return resp;
        },
        MAX_RETRIES,
        BASE_DELAY,
      );
    } catch (e) {
      telemetry.trackError({ message: 'Error generating patient summary', error: e });
      throw new Error('Error generating summary');
    } finally {
      setIsGenerating(false);
    }
  };

  const regenerateSummary = async (summaryType: string | null) => {
    if (!summaryType) {
      return;
    }
    setIsGenerating(true);
    try {
      const upid = patient.UPID || 'unknown-upid';
      // Get the new summary directly from the POST response
      const newSummary = await triggerSummaryGeneration(
        ctwFetch,
        requestContext,
        upid,
        summaryType,
        useNewAIApi,
      );

      // Force a refetch to update the cache, but don't wait for it or use its results
      void refetch();

      // Return the already-generated summary from the POST
      return { data: newSummary };
    } catch (e) {
      telemetry.trackError({ message: 'Error regenerating patient summary', error: e });
      throw new Error('Error regenerating summary');
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    document,
    isLoading,
    isGenerating,
    error,
    getSummary,
    regenerateSummary,
  };
};
